import React from 'react';
import {
  PopoverBody,
  PopoverHeader,
  Text,
  PopoverProps,
} from '@aag-development/becreatives-ui';
import styled from 'styled-components';
import RevisionVersionTooltipPng from 'src/assets/images/revision-version-tooltip-image.png';
import { RevisionVersionsPopover } from './RevisionsContentMain.styled';
import { useToggle } from 'src/helpers/useToggle';
import { HelpCircle } from 'lucide-react';
import { cn } from 'src/lib/utils';

const PopoverImage = styled.img.attrs((props) => ({ ...props, src: RevisionVersionTooltipPng }))`
  max-width: 100%;
  padding: 1rem 0;
`;

const RevisionVersionTooltipPopover: React.FC<Omit<PopoverProps, 'target'>> = (props) => {
  const [isOpen, setOpen, toggle] = useToggle();

  return (
    <>
      <button
        id={'RevisionVersionTooltipPopover'}
        type={'button'}
        onMouseLeave={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onTouchEnd={() => setOpen(false)}
        onTouchStart={() => setOpen(true)}
        className={cn(
          'tw-inline-flex tw-items-center tw-justify-center tw-rounded-full',
          'tw-h-8 tw-w-8 tw-transition-all tw-duration-200',
          'tw-bg-brand-50 tw-text-brand-600 hover:tw-bg-brand-100 hover:tw-text-brand-700',
          'focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500 focus:tw-ring-offset-2',
          'tw-cursor-pointer'
        )}
        aria-label="Version help"
      >
        <HelpCircle className={'tw-h-5 tw-w-5'} />
      </button>

      <RevisionVersionsPopover
        target={'RevisionVersionTooltipPopover'}
        isOpen={isOpen}
        toggle={toggle}
        {...props}
      >
        <PopoverHeader>
          <div className={'tw-flex tw-items-center tw-gap-2'}>
            <HelpCircle className={'tw-h-5 tw-w-5 tw-text-brand-600'} />
            <Text tag={'b'} className={'tw-text-lg'}>How Versions Work</Text>
          </div>
        </PopoverHeader>
        <PopoverBody>
          <div className={'tw-space-y-3'}>
            <Text color={'gray'} className={'tw-text-sm'}>
              Your editor submits multiple versions of your video as they refine and perfect your content.
            </Text>
            <div className={'tw-space-y-2'}>
              <div className={'tw-flex tw-items-start tw-gap-2'}>
                <div className={'tw-mt-1 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-brand-600'} />
                <Text color={'gray'} className={'tw-text-sm tw-flex-1'}>
                  Use the <strong>dropdown menu</strong> to quickly jump to any version
                </Text>
              </div>
              <div className={'tw-flex tw-items-start tw-gap-2'}>
                <div className={'tw-mt-1 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-brand-600'} />
                <Text color={'gray'} className={'tw-text-sm tw-flex-1'}>
                  Navigate using the <strong>Previous/Next buttons</strong> below the video
                </Text>
              </div>
              <div className={'tw-flex tw-items-start tw-gap-2'}>
                <div className={'tw-mt-1 tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-brand-600'} />
                <Text color={'gray'} className={'tw-text-sm tw-flex-1'}>
                  Review each version and provide feedback in the chat
                </Text>
              </div>
            </div>
            <PopoverImage />
            <div className={'tw-rounded-md tw-bg-brand-50 tw-p-3'}>
              <Text className={'tw-text-xs tw-text-brand-800'}>
                💡 <strong>Tip:</strong> The latest version is always selected by default. Compare versions to track your project's progress!
              </Text>
            </div>
          </div>
        </PopoverBody>
      </RevisionVersionsPopover>
    </>
  );
};

export { RevisionVersionTooltipPopover };
